import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { FlowInstance } from '../models/flow.model';
import { BaseService } from './base.service';
import { IBaseModel } from '../models/base.model';
import { FlowInstanceServiceGetParams } from './interfaces/flow-instance.interface';
import { FlowObjectDefinition, FlowObjectInstance } from '../models/flow-object.model';
import { firstValueFrom } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class FlowInstanceService extends BaseService {
    controllerRoute = 'FlowInstance';

    constructor(httpClient: HttpClient) {
        super(httpClient);
    }

    public async get(getParams?: FlowInstanceServiceGetParams): Promise<IBaseModel<FlowInstance[]>> {
        let httpParams = new HttpParams();

        if (getParams?.version != null) httpParams = httpParams.append('version', getParams.version.toString());
        if (getParams?.flowDefinitionId != null) httpParams = httpParams.append('flowDefinitionId', getParams.flowDefinitionId);
        if (getParams?.stateId != null) httpParams = httpParams.append('stateId', getParams.stateId.toString());

        const url = `${this.apiBaseUrl}/${this.controllerRoute}`;
        return await firstValueFrom(this.httpClient.get<IBaseModel<FlowInstance[]>>(url, { params: httpParams }));
    }

    public async getById(id: string, version?: number): Promise<IBaseModel<FlowInstance>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}/${id}/${(version || '')}`;
        return await firstValueFrom(this.httpClient.get<IBaseModel<FlowInstance>>(url));
    }

    public async filterPaginated(getParams?: FlowInstanceServiceGetParams): Promise<IBaseModel<FlowInstance[]>> {
        let httpParams = new HttpParams();

        if (getParams?.page != null) httpParams = httpParams.append('page', getParams.page.toString());
        if (getParams?.pageSize != null) httpParams = httpParams.append('pageSize', getParams.pageSize.toString());
        if (getParams?.sortField != null) httpParams = httpParams.append('sortField', getParams.sortField);
        if (getParams?.isSortDescending != null) httpParams = httpParams.append('isSortDescending', getParams.isSortDescending.toString());
        if (getParams?.skipDataResults != null) httpParams = httpParams.append('skipDataResults', getParams.skipDataResults.toString());
        if (getParams?.searchTokens != null && getParams.searchTokens.length > 0) {
            httpParams = httpParams.append('searchTokens', getParams.searchTokens.map(x => x.trim()).join('◄►'));
        }

        const url = `${this.apiBaseUrl}/${this.controllerRoute}/FilterPaginated`;
        return await firstValueFrom(this.httpClient.get<IBaseModel<FlowInstance[]>>(url, { params: httpParams }));
    }

    public async create(flowObjectDefinition: FlowObjectDefinition, inputData: any): Promise<IBaseModel<FlowInstance>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}`;
        return await firstValueFrom(this.httpClient.post<IBaseModel<FlowInstance>>(
            url,
            { flowObjectDefinition, inputData: JSON.stringify(inputData) },
            { headers: { 'X-Request-Source': window.location.href } }
        ));
    }

    public async manuallyFinishOrCancel(data: FlowObjectInstance): Promise<IBaseModel<void>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}/ManuallyFinishOrCancel`;
        return await firstValueFrom(this.httpClient.put<IBaseModel<void>>(url, data));
    }
    
    public async cancelByDeliberation(data: FlowObjectInstance): Promise<IBaseModel<void>> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}/CancelByDeliberation`;
        return await firstValueFrom(this.httpClient.put<IBaseModel<void>>(url, data));
    }
    
    public async getServerTime(): Promise<Date> {
        const url = `${this.apiBaseUrl}/${this.controllerRoute}/GetServerTime`;
        return await firstValueFrom(this.httpClient.get<Date>(url));
    }

}
